import React, { useEffect, useState } from "react";
import { Paper, Typography, IconButton, Tooltip } from "@mui/material";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import classes from "./myteam.module.css";
import { EMERGENCY_CONTACT } from "../../../api-calls/api-urls";
import {
  authApi,
  getAuthorizationHeader,
} from "../../../api-calls/user-requests";

const RELATIONSHIP_LABELS = {
  spouse: "Spouse",
  parent: "Parent",
  sibling: "Sibling",
  child: "Child",
  friend: "Friend",
  other: "Other",
};

const formatPhone = (phone) => {
  if (!phone?.number) return "";
  const n = phone.number;
  const pretty =
    phone.country_code === "+91" && n.length === 10
      ? `${n.slice(0, 5)} ${n.slice(5)}`
      : n;
  return `${phone.country_code} ${pretty}`;
};

// A member's emergency contact, read-only for the leader. The backend only
// returns it to a leader of a company the member belongs to; on any error
// (e.g. a former member) the card is left out.
const MemberEmergencyContactCard = ({ userId, ln = {} }) => {
  const [contact, setContact] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setState("loading");
    authApi
      .get(`${EMERGENCY_CONTACT}/${userId}`, {
        headers: { Authorization: getAuthorizationHeader() },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setContact(data?.data || null);
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!userId || state === "error") return null;

  const relationship =
    contact?.relationship === "other" && contact?.relationship_other
      ? contact.relationship_other
      : ln[`relationship_${contact?.relationship}`] ||
        RELATIONSHIP_LABELS[contact?.relationship] ||
        "";

  const phoneRow = (phone, secondary) => (
    <div className={classes.emergencyRow} key={secondary ? "s" : "p"}>
      <PhoneOutlinedIcon sx={{ fontSize: 18, color: "#6b7280" }} />
      <div className={classes.emergencyPhoneText}>
        <Typography className={classes.emergencyValue}>
          {formatPhone(phone)}
        </Typography>
        {secondary && (
          <Typography className={classes.emergencyMuted}>
            {ln.secondary || "Secondary"}
          </Typography>
        )}
      </div>
      <Tooltip title={ln.call || "Call"}>
        <IconButton
          size="small"
          className={classes.emergencyCall}
          href={`tel:${phone.country_code}${phone.number}`}
          aria-label={ln.call || "Call"}
        >
          <PhoneInTalkOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <Paper className={classes.filesCard} elevation={0}>
      <div className={classes.filesCardHeader} style={{ justifyContent: "flex-start", columnGap: 8 }}>
        <ReportGmailerrorredIcon sx={{ fontSize: 22, color: "#dc2626" }} />
        <Typography className={classes.filesCardTitle}>
          {ln.emergency_contact || "Emergency Contact"}
        </Typography>
      </div>

      {state === "loading" ? (
        <Typography className={classes.filesCardEmpty}>
          {ln.loading || "Loading…"}
        </Typography>
      ) : !contact ? (
        <Typography className={classes.filesCardEmpty}>
          {ln.no_emergency_contact || "No emergency contact added"}
        </Typography>
      ) : (
        <>
          <div className={classes.emergencyName}>
            <Typography className={classes.emergencyNameText}>
              {contact.name}
            </Typography>
            {relationship && (
              <span className={classes.emergencyChip}>{relationship}</span>
            )}
          </div>
          {phoneRow(contact.phone, false)}
          {contact.secondary_phone?.number
            ? phoneRow(contact.secondary_phone, true)
            : null}
          {contact.email ? (
            <div className={classes.emergencyRow}>
              <MailOutlineIcon sx={{ fontSize: 18, color: "#6b7280" }} />
              <Typography className={classes.emergencyValue} title={contact.email}>
                {contact.email}
              </Typography>
            </div>
          ) : null}
        </>
      )}
    </Paper>
  );
};

export default MemberEmergencyContactCard;
